
import React, { useState, useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const educationSchema = z.object({
  school: z.string().min(1, { message: "School is required" }),
  degree: z.string().min(1, { message: "Degree is required" }),
  fieldOfStudy: z.string().min(1, { message: "Field of study is required" }),
  graduationYear: z.string().min(1, { message: "Graduation year is required" }),
});

const experienceSchema = z.object({
  company: z.string().min(1, { message: "Company is required" }),
  title: z.string().min(1, { message: "Title is required" }),
  yearsExperience: z.string().min(1, { message: "Years of experience is required" }),
});

const profileSchema = z.object({
  bio: z.string().min(10, { message: "Bio should be at least 10 characters" }),
  expertise: z.string().min(1, { message: "Areas of expertise is required" }),
  educations: z.array(educationSchema).min(1, { message: "At least one education is required" }),
  experiences: z.array(experienceSchema).min(1, { message: "At least one experience is required" }),
});

const CompleteProfile = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to complete your profile",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }

      // Check if user already has a profile
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      setUserProfile(profileData);
    };

    checkUser();
  }, [navigate, toast]);

  const form = useForm({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      bio: "",
      expertise: "",
      educations: [{ school: "", degree: "", fieldOfStudy: "", graduationYear: "" }],
      experiences: [{ company: "", title: "", yearsExperience: "" }],
    },
  });

  const { fields: educationFields, append: appendEducation, remove: removeEducation } = useFieldArray({
    control: form.control,
    name: "educations"
  });

  const { fields: experienceFields, append: appendExperience, remove: removeExperience } = useFieldArray({
    control: form.control,
    name: "experiences"
  });

  const handleSubmit = async (data: z.infer<typeof profileSchema>) => {
    setIsLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication error",
          description: "You must be logged in to complete your profile.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      
      // First, update the user type and bio in the profiles table
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({
          user_type: 'alumni',
          bio: data.bio
        })
        .eq('id', user.id);
        
      if (profileUpdateError) {
        throw profileUpdateError;
      }
      
      // Check if an alumni profile already exists
      const { data: existingAlumniProfile } = await supabase
        .from('alumni_profiles')
        .select('id')
        .eq('id', user.id)
        .single();
      
      // If alumni profile doesn't exist, create it
      if (!existingAlumniProfile) {
        const { error: insertError } = await supabase
          .from('alumni_profiles')
          .insert([{ 
            id: user.id,
            title: data.experiences[0].title,
            company: data.experiences[0].company,
            years_experience: parseInt(data.experiences[0].yearsExperience),
            school: data.educations[0].school,
            degree: data.educations[0].degree,
            field_of_study: data.educations[0].fieldOfStudy,
            graduation_year: parseInt(data.educations[0].graduationYear),
            expertise: data.expertise
          }]);
        
        if (insertError) {
          throw insertError;
        }
      } else {
        // Update existing alumni profile
        const { error: updateError } = await supabase
          .from('alumni_profiles')
          .update({
            title: data.experiences[0].title,
            company: data.experiences[0].company,
            years_experience: parseInt(data.experiences[0].yearsExperience),
            school: data.educations[0].school,
            degree: data.educations[0].degree,
            field_of_study: data.educations[0].fieldOfStudy,
            graduation_year: parseInt(data.educations[0].graduationYear),
            expertise: data.expertise
          })
          .eq('id', user.id);
          
        if (updateError) {
          throw updateError;
        }
      }
      
      toast({
        title: "Profile completed",
        description: "Your alumni profile has been created successfully.",
      });
      
      // Navigate to services setup
      navigate("/services-setup");
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error updating profile",
        description: error.message || "There was an error completing your profile",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white py-10 font-montserrat">
      <div className="container max-w-3xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl font-bold text-crimson">Complete Your Alumni Profile</h1>
          <p className="text-gray-600 mt-2">Tell us about your experience and expertise</p>
        </div>
        
        <Card className="bg-white border border-gray-200">
          <CardHeader>
            <CardTitle>Professional Information</CardTitle>
            <CardDescription>
              This information will be displayed on your public profile and helps students find you
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                <div className="space-y-4 pt-4">
                  <h3 className="font-medium text-lg">About You</h3>
                  
                  <FormField
                    control={form.control}
                    name="bio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bio</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Tell students about yourself, your career journey, and how you can help them..." 
                            className="min-h-32 bg-white border border-gray-200"
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          This will be displayed on your public profile.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="expertise"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Areas of Expertise</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="e.g. Resume Review, Career Advice, Interview Preparation" 
                            className="bg-white border border-gray-200"
                            {...field} 
                          />
                        </FormControl>
                        <FormDescription>
                          Separate multiple areas with commas
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-lg">Education</h3>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => appendEducation({ 
                        school: "", 
                        degree: "", 
                        fieldOfStudy: "", 
                        graduationYear: "" 
                      })}
                      className="flex items-center border border-gray-200"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Education
                    </Button>
                  </div>
                  
                  {educationFields.map((field, index) => (
                    <div key={field.id} className="p-4 border border-gray-200 rounded-md space-y-4 bg-white">
                      {index > 0 && (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeEducation(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        </div>
                      )}
                      
                      <FormField
                        control={form.control}
                        name={`educations.${index}.school`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>School/University</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Enter your school or university name"
                                className="bg-white border border-gray-200"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name={`educations.${index}.degree`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Degree</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g. Bachelor's, Master's"
                                  className="bg-white border border-gray-200"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`educations.${index}.graduationYear`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Graduation Year</FormLabel>
                              <FormControl>
                                <Input 
                                  placeholder="e.g. 2018" 
                                  className="bg-white border border-gray-200"
                                  {...field} 
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      
                      <FormField
                        control={form.control}
                        name={`educations.${index}.fieldOfStudy`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Field of Study</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g. Computer Science, Business"
                                className="bg-white border border-gray-200"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
                </div>
                
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-lg">Professional Experience</h3>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={() => appendExperience({ 
                        company: "", 
                        title: "", 
                        yearsExperience: "" 
                      })}
                      className="flex items-center border border-gray-200"
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Experience
                    </Button>
                  </div>
                  
                  {experienceFields.map((field, index) => (
                    <div key={field.id} className="p-4 border border-gray-200 rounded-md space-y-4 bg-white">
                      {index > 0 && (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeExperience(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                          </Button>
                        </div>
                      )}
                      
                      <FormField
                        control={form.control}
                        name={`experiences.${index}.company`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Enter company name"
                                className="bg-white border border-gray-200"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`experiences.${index}.title`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Professional Title</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="e.g. Software Engineer" 
                                className="bg-white border border-gray-200"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`experiences.${index}.yearsExperience`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Years of Experience</FormLabel>
                            <FormControl>
                              <Input 
                                type="number" 
                                min="0" 
                                max="50" 
                                placeholder="e.g. 5" 
                                className="bg-white border border-gray-200"
                                {...field} 
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  ))}
                </div>
                
                <div className="pt-4 flex justify-end space-x-4">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="border border-gray-200"
                  >
                    Save for Later
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-crimson hover:bg-crimson-dark"
                    disabled={isLoading}
                  >
                    {isLoading ? "Saving..." : "Continue to Services Setup"}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompleteProfile;
